import {
    useCallback,
    useEffect,
    useRef,
    useState,
} from 'react';

import Location from '@r6digital/rapidstor-types/react/entities/Location';

import Logger from '@/core/utils/logging';
import {
    SettlementRoomType,
    SettlementSocketData,
    SocketPayload,
    SocketStatus,
} from '@/core/types/Socket';

import SocketService from '@/features/rapidstor/services/SocketService';

export interface SettlementCompletionHandlers<T extends SettlementRoomType> {
    onSuccess?: (result: SocketPayload<T>) => void;
    onFailure?: (result: SocketPayload<T>) => void;
    onTimeout?: () => void;
}

interface UseSettlementSocketParams<T extends SettlementRoomType> {
    location: Location;
    /** Which room family to join — decides the completion event listened for. */
    roomType: T;
    /** The room key's prefix; the room joined is `{roomPrefix}:{reservationId}`. */
    roomPrefix: string;
    /** Prefix for this hook's log lines, e.g. `[useMoveInSocket]`. */
    logPrefix: string;
}

export interface UseSettlementSocketReturn<T extends SettlementRoomType> {
    startListening: (
        reservationId: string,
        handlers?: SettlementCompletionHandlers<T>,
    ) => void;
    stopListening: () => void;
    latestResult: SocketPayload<T> | null;
    status: SocketStatus;
}

/**
 * Awaits a settlement the backend finalises after the browser has handed off to a payment
 * gateway — the gateway takes the money, calls a webhook, and the API publishes the outcome
 * to a room keyed by reservationId. The server stores the latest result and replays it on
 * join, so joining late still resolves. The socket is torn down on unmount and by the
 * service's internal timeout.
 *
 * Room-specific hooks (`useMoveInSocket`, `useReservationFeeSocket`) wrap this with their
 * own room type and prefix; extending to a new settlement room needs nothing here.
 */
export const useSettlementSocket = <T extends SettlementRoomType>({
    location,
    roomType,
    roomPrefix,
    logPrefix,
}: UseSettlementSocketParams<T>): UseSettlementSocketReturn<T> => {
    const serviceRef = useRef<SocketService | null>(null);
    const [latestResult, setLatestResult] = useState<SocketPayload<T> | null>(null);
    const [status, setStatus] = useState<SocketStatus>('idle');

    const getService = useCallback((): SocketService => {
        if (!serviceRef.current) {
            serviceRef.current = new SocketService(location);
        }
        return serviceRef.current;
    }, [location]);

    const startListening = useCallback((
        reservationId: string,
        handlers?: SettlementCompletionHandlers<T>,
    ): void => {
        if (!reservationId) {
            return;
        }
        getService().listen<T>({
            room: `${roomPrefix}:${reservationId}`,
            roomType,
            onEvent: (payload) => {
                setLatestResult(payload);
                setStatus('received');
                Logger.info(
                    `${logPrefix} ${roomType} event received: ${JSON.stringify(payload)}`,
                    location,
                    [{ name: 'rapidstor_reservationId', value: reservationId }],
                );
                // Every SettlementRoomType payload carries `success`; the cast is only
                // needed because `SocketPayload<T>` stays generic until T is resolved.
                if ((payload as SettlementSocketData).success) {
                    handlers?.onSuccess?.(payload);
                } else {
                    handlers?.onFailure?.(payload);
                }
            },
            onTimeout: () => {
                setStatus('timeout');
                Logger.warn(
                    `${logPrefix} ${roomType} socket timed out before completion`,
                    location,
                    [{ name: 'rapidstor_reservationId', value: reservationId }],
                );
                handlers?.onTimeout?.();
            },
        });
        setStatus('listening');
    }, [getService, location, logPrefix, roomPrefix, roomType]);

    const stopListening = useCallback((): void => {
        serviceRef.current?.disconnect();
    }, []);

    useEffect(() => () => {
        serviceRef.current?.disconnect();
    }, []);

    return {
        startListening,
        stopListening,
        latestResult,
        status,
    };
};

export default useSettlementSocket;
